import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { guideItems } from '../data/guides';
import { getLocalizedValue } from '../config/languageConfig';
import { trackMainBannerClick } from '../services/AnalyticsService';

const GuideBanner: React.FC = () => {
  const navigate = useNavigate();
  const [currentIndex, setCurrentIndex] = useState(0);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  
  // 터치 이벤트 상태
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);

  // 현재 언어에 따른 이미지 URL 가져오기
  const getLocalizedImage = (guide: typeof guideItems[0]) => {
    return getLocalizedValue(guide.image, {
      en: guide.imageEn,
      ja: guide.imageJa,
      'zh-CN': guide.imageZhCn,
      'zh-TW': guide.imageZhTw
    });
  };

  // 자동 롤링 시작
  const startAutoRoll = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    
    intervalRef.current = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % guideItems.length);
    }, 4000); // 4초마다 자동 전환
  };

  // 자동 롤링 정지
  const stopAutoRoll = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  // 다음 슬라이드로 이동
  const goToNext = () => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % guideItems.length);
  };

  // 이전 슬라이드로 이동
  const goToPrevious = () => {
    setCurrentIndex((prevIndex) => 
      prevIndex === 0 ? guideItems.length - 1 : prevIndex - 1
    );
  };

  // 터치 시작
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
    stopAutoRoll();
  };

  // 터치 이동
  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
    
    // 수평 스와이프인 경우 기본 스크롤 동작 방지
    if (touchStart !== null) {
      const currentTouch = e.targetTouches[0].clientX;
      const distance = Math.abs(touchStart - currentTouch);
      
      // 수평 이동이 10px 이상이면 기본 동작 방지
      if (distance > 10) {
        e.preventDefault();
      }
    }
  };

  // 터치 종료
  const handleTouchEnd = (e: React.TouchEvent) => {
    if (!touchStart || !touchEnd) {
      startAutoRoll();
      return;
    }
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe && guideItems.length > 1) {
      goToNext();
      e.preventDefault(); // 기본 동작 방지
    }
    if (isRightSwipe && guideItems.length > 1) {
      goToPrevious();
      e.preventDefault(); // 기본 동작 방지
    }
    
    // 터치 상태 초기화
    setTouchStart(null);
    setTouchEnd(null);
    
    // 자동 롤링 재시작
    if (guideItems.length > 1) {
      startAutoRoll();
    }
  };

  // 컴포넌트 마운트 시 자동 롤링 시작
  useEffect(() => {
    if (guideItems.length > 1) {
      startAutoRoll();
    }
    
    return () => {
      stopAutoRoll();
    };
  }, []);

  // 언어 변경 감지하여 리렌더링
  useEffect(() => {
    const handleLanguageChange = () => {
      // 언어 변경 시 강제 리렌더링을 위해 상태 업데이트
      setCurrentIndex(prevIndex => prevIndex);
    };

    window.addEventListener('languageChange', handleLanguageChange);
    return () => {
      window.removeEventListener('languageChange', handleLanguageChange);
    };
  }, []);

  // 가이드 클릭 핸들러
  const handleGuideClick = (guideId: string, bannerIndex: number) => {
    // GA 이벤트 전송 (배너의 노출 순위는 1부터 시작)
    trackMainBannerClick(bannerIndex + 1);
    
    navigate(`/guides/${guideId}`);
  };

  // 마우스 호버 시 자동 롤링 정지/재시작
  const handleMouseEnter = () => {
    stopAutoRoll();
  };

  const handleMouseLeave = () => {
    if (guideItems.length > 1) {
      startAutoRoll();
    }
  };

  // 인디케이터 클릭 핸들러
  const handleIndicatorClick = (index: number) => {
    setCurrentIndex(index);
    // 수동으로 변경한 후 자동 롤링 재시작
    stopAutoRoll();
    if (guideItems.length > 1) {
      setTimeout(() => startAutoRoll(), 1000); // 1초 후 재시작
    }
  };

  if (guideItems.length === 0) {
    return null;
  }

  return (
    <div className="">
      
      <div 
        ref={containerRef}
        className="relative overflow-hidden touch-pan-y"
        style={{ touchAction: 'pan-y pinch-zoom' }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div 
          className="flex transition-transform duration-500 ease-in-out"
          style={{
            transform: `translateX(-${currentIndex * 100}%)`,
          }}
        >
          {guideItems.map((guide, index) => (
            <div
              key={guide.id}
              className="w-full flex-shrink-0 cursor-pointer"
              onClick={() => handleGuideClick(guide.id, index)}
            >
              <img
                src={getLocalizedImage(guide)}
                alt={`Guide ${index + 1}`}
                className="w-full h-32 object-cover transition-transform duration-300 hover:scale-105"
                style={{
                  objectPosition: index === 3 ? 'center 68%' : 'center center'
                }}
                loading="lazy"
                draggable={false}
              />
            </div>
          ))}
        </div>
        
        {/* 인디케이터 - 가이드가 2개 이상일 때만 표시 */}
        {guideItems.length > 1 && (
          <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 flex space-x-1 bg-black/20 backdrop-blur-sm px-2 py-1 rounded-full">
            {guideItems.map((_, index) => (
              <button
                key={index}
                className={`w-1.5 h-1.5 rounded-full transition-all duration-300 ${
                  index === currentIndex 
                    ? 'bg-white scale-110' 
                    : 'bg-white/40'
                }`}
                onClick={(e) => {
                  e.stopPropagation();
                  handleIndicatorClick(index);
                }}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default GuideBanner; 